'use client';

/**
 * PlannerBottomSheet — the trip planner on a phone: a sheet over a live map.
 *
 *   ┌ map (full height, always live) ───────────────┐
 *   │                                               │
 *   │  ┌ sheet ─────────────────────────────────┐   │  full: under the top bar
 *   │  │ ━━  grab handle                         │   │  half: top near mid-screen
 *   │  │ planner (summary · ladder · actions)    │   │  peek: summary + actions
 *   └──┴─────────────────────────────────────────┴───┘
 *
 * - Three snap points. Drag the handle (touch, mouse or pen), drag the
 *   content (below full it moves the sheet, not the list; at full a pull
 *   down from the top of the list collapses it), tap the handle, or use the
 *   arrow keys on it.
 * - The map never resizes: the sheet sits over it. Its live height is
 *   published as `--tp-sheet-h` on the parent so map controls ride just
 *   above it, and `onInsetChange` reports the settled height so the map can
 *   frame the trip in the part you can see.
 * - Half never slices a row: its height is nudged (a few px either way) so
 *   the list's bottom edge lands between rows ([data-sheet-row] inside
 *   [data-sheet-body]), never through a stop or its night stepper.
 * - Motion uses the tp tokens (320ms, ease-tp-out); prefers-reduced-motion
 *   snaps without animating.
 * - At full the sheet holds keyboard focus (Tab wraps); Escape drops it to
 *   half and gives the map back.
 * - While mounted, <html data-planner-sheet> tells the rest of the page
 *   (site header, footer, the floating assistant) to step aside.
 */
import React from 'react';
import {
  type SheetSnap,
  PEEK_HEIGHT,
  alignHeightToRows,
  clampWithResistance,
  releaseVelocity,
  resolveSnap,
  snapHeights,
  stepSnap,
  tapSnap,
} from './sheetSnap';

export interface PlannerBottomSheetProps {
  snap: SheetSnap;
  onSnap: (snap: SheetSnap) => void;
  /** Settled height in px (after each snap animation), for map framing. */
  onInsetChange?: (height: number) => void;
  peekHeight?: number;
  /** Shown to assistive tech as the sheet's name. */
  label?: string;
  className?: string;
  children: React.ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const NO_DRAG = 'input, textarea, select, [contenteditable="true"], [data-sheet-nodrag], [role="slider"]';

// Layout effects only in the browser (the sheet is also rendered on the
// server, where React warns about them).
const useIsoLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

function scrollableAncestor(from: HTMLElement | null, stop: HTMLElement): HTMLElement | null {
  for (let el = from; el && el !== stop; el = el.parentElement) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
  }
  return null;
}

export default function PlannerBottomSheet({
  snap,
  onSnap,
  onInsetChange,
  peekHeight = PEEK_HEIGHT,
  label = 'Trip planner',
  className = '',
  children,
}: PlannerBottomSheetProps) {
  const ref = React.useRef<HTMLElement>(null);
  // The home-bar inset (iPhone): the action bar pads itself above it, so the
  // peek grows by the same amount instead of clipping the buttons.
  const [safeBottom, setSafeBottom] = React.useState(0);
  React.useEffect(() => {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom)';
    document.body.appendChild(probe);
    const px = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
    probe.remove();
    // The bar already keeps 8px under its buttons; only the rest is extra.
    setSafeBottom(Math.max(0, Math.round(px) - 8));
  }, []);
  const [box, setBox] = React.useState(() => ({
    container: typeof window === 'undefined' ? 0 : window.innerHeight,
    viewport: typeof window === 'undefined' ? 0 : window.innerHeight,
  }));
  // Peek fits what the planner shows there (its header and action bar,
  // marked [data-sheet-peek-part]), measured while at peek; `peekHeight` is
  // the fallback (and the empty planner's own peek).
  const [contentPeek, setContentPeek] = React.useState(0);
  const peekH = contentPeek > 0 ? contentPeek : peekHeight + safeBottom;
  const heights = React.useMemo(
    () => snapHeights(box.container, box.viewport, peekH),
    [box.container, box.viewport, peekH]
  );
  // Half, nudged onto a row boundary (see alignHeightToRows).
  const [halfAdjust, setHalfAdjust] = React.useState(0);
  const snapH = React.useMemo(
    () => ({ ...heights, half: Math.max(heights.peek, Math.min(heights.full, heights.half + halfAdjust)) }),
    [heights, halfAdjust]
  );
  const [dragH, setDragH] = React.useState<number | null>(null);
  const height = dragH ?? snapH[snap];

  // Latest values for the native listeners (bound once).
  const live = React.useRef({ snap, heights: snapH, height, onSnap });
  live.current = { snap, heights: snapH, height, onSnap };

  // The map area's size (the sheet's parent).
  React.useEffect(() => {
    const parent = ref.current?.parentElement;
    if (!parent) return;
    const measure = () => setBox({ container: parent.clientHeight, viewport: window.innerHeight });
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(parent);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el || snap !== 'peek') return;
    const measure = () => {
      if (el.hasAttribute('data-dragging')) return;
      const parts = Array.from(el.querySelectorAll<HTMLElement>('[data-sheet-peek-part]')).filter((n) => n.offsetParent !== null);
      if (!parts.length) {
        setContentPeek(0);
        return;
      }
      const handle = el.querySelector<HTMLElement>('[data-sheet-handle]')?.offsetHeight ?? 24;
      const sum = Math.ceil(parts.reduce((a, n) => a + n.getBoundingClientRect().height, 0) + handle);
      setContentPeek((prev) => (Math.abs(prev - sum) <= 1 ? prev : sum));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    el.querySelectorAll<HTMLElement>('[data-sheet-peek-part]').forEach((n) => ro.observe(n));
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(measure) : null;
    mo?.observe(el, { childList: true, subtree: true });
    return () => {
      ro.disconnect();
      mo?.disconnect();
    };
  }, [snap]);

  // Tell the page the sheet is up (site chrome and the floating assistant
  // step aside; the page underneath stops scrolling).
  React.useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-planner-sheet', '');
    return () => {
      root.removeAttribute('data-planner-sheet');
    };
  }, []);

  // Measure where the list's rows sit and nudge half so none is cut. Runs
  // when the sheet settles at half, and again when the list changes (a stop
  // added, a stepper appearing) or is scrolled.
  const alignHalf = React.useCallback(() => {
    const el = ref.current;
    if (!el || live.current.snap !== 'half' || el.hasAttribute('data-dragging')) return;
    const body = el.querySelector<HTMLElement>('[data-sheet-body]');
    if (!body || body.offsetParent === null) {
      setHalfAdjust(0);
      return;
    }
    const s = el.getBoundingClientRect();
    const b = body.getBoundingClientRect();
    const footer = s.bottom - b.bottom;
    const rows = Array.from(body.querySelectorAll<HTMLElement>('[data-sheet-row]'))
      .map((r) => {
        const q = r.getBoundingClientRect();
        return { top: q.top - s.top, bottom: q.bottom - s.top };
      })
      .filter((r) => r.bottom - r.top > 4);
    const base = heights.half;
    const next = alignHeightToRows(base, rows, footer, {
      maxShrink: 64,
      maxGrow: 40,
      min: heights.peek + 80,
      max: heights.full,
    });
    setHalfAdjust((prev) => (prev === next - base ? prev : next - base));
  }, [heights]);
  useIsoLayoutEffect(() => {
    if (snap !== 'half' || dragH !== null) return;
    alignHalf();
    const el = ref.current;
    const body = el?.querySelector<HTMLElement>('[data-sheet-body]');
    if (!body) return;
    let t = 0;
    const later = () => {
      window.clearTimeout(t);
      t = window.setTimeout(alignHalf, 140);
    };
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(later) : null;
    mo?.observe(body, { childList: true, subtree: true, characterData: true });
    body.addEventListener('scroll', later, { passive: true });
    // Once more after the snap animation: the header above the list grows
    // as the sheet leaves peek (the add-a-stop box), moving every row.
    const settled = window.setTimeout(alignHalf, 400);
    return () => {
      window.clearTimeout(settled);
      window.clearTimeout(t);
      mo?.disconnect();
      body.removeEventListener('scroll', later);
    };
  }, [snap, dragH, alignHalf]);

  const publish = React.useCallback((h: number) => {
    ref.current?.parentElement?.style.setProperty('--tp-sheet-h', `${Math.round(h)}px`);
  }, []);

  // Follow the height animation frame by frame so anything riding the
  // sheet's top (map controls, the pin banner) moves with it; then report
  // the settled height once.
  const onInsetRef = React.useRef(onInsetChange);
  onInsetRef.current = onInsetChange;
  React.useEffect(() => {
    if (dragH !== null) return;
    const target = snapH[snap];
    let raf = 0;
    const t0 = performance.now();
    const tick = () => {
      const el = ref.current;
      if (el) publish(el.getBoundingClientRect().height);
      if (performance.now() - t0 < 380) raf = requestAnimationFrame(tick);
      else {
        publish(target);
        onInsetRef.current?.(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [snap, snapH, dragH, publish]);

  // Touch: drag the sheet by its content. Native listeners (passive: false)
  // so a vertical drag below full can stop the list from scrolling.
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    type Gesture = {
      x: number;
      y: number;
      h: number;
      scroller: HTMLElement | null;
      mode: null | 'sheet' | 'native';
      samples: { t: number; y: number }[];
    };
    let g: Gesture | null = null;
    let current = 0;
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        g = null;
        return;
      }
      const target = e.target as HTMLElement | null;
      if (target?.closest(NO_DRAG)) {
        g = null;
        return;
      }
      const t = e.touches[0];
      g = {
        x: t.clientX,
        y: t.clientY,
        h: live.current.height,
        scroller: scrollableAncestor(target, el),
        mode: null,
        samples: [{ t: e.timeStamp, y: t.clientY }],
      };
    };
    const onMove = (e: TouchEvent) => {
      if (!g || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - g.x;
      const dy = t.clientY - g.y;
      const { snap: s, heights: hs } = live.current;
      if (!g.mode) {
        // Below full, a vertical drag belongs to the sheet from the first
        // pixel (the browser must not start scrolling the list).
        if (s !== 'full' && Math.abs(dy) >= Math.abs(dx) && e.cancelable) e.preventDefault();
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dx) > Math.abs(dy)) {
          g.mode = 'native'; // a carousel swipe
          return;
        }
        const atTop = !g.scroller || g.scroller.scrollTop <= 0;
        g.mode = s !== 'full' || (dy > 0 && atTop) ? 'sheet' : 'native';
        if (g.mode === 'sheet') {
          g.y = t.clientY;
          g.h = live.current.height;
          el.setAttribute('data-dragging', 'true');
        }
      }
      if (g.mode !== 'sheet') return;
      if (e.cancelable) e.preventDefault();
      current = clampWithResistance(g.h - (t.clientY - g.y), hs.peek, hs.full);
      setDragH(current);
      publish(current);
      g.samples.push({ t: e.timeStamp, y: t.clientY });
      if (g.samples.length > 8) g.samples.shift();
    };
    const onEnd = (e: TouchEvent) => {
      if (g?.mode === 'sheet') {
        const { snap: s, heights: hs, onSnap: set } = live.current;
        const v = releaseVelocity(g.samples, e.timeStamp);
        el.removeAttribute('data-dragging');
        setDragH(null);
        set(resolveSnap(current, v, s, hs));
        snapTick();
      }
      g = null;
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [publish]);

  // Mouse / pen: drag the handle. (Touch is handled above for the whole
  // sheet, handle included.)
  const mouse = React.useRef<{ y: number; h: number; moved: boolean; samples: { t: number; y: number }[] } | null>(null);
  const onHandlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'touch' || e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    mouse.current = { y: e.clientY, h: height, moved: false, samples: [{ t: e.timeStamp, y: e.clientY }] };
  };
  const onHandlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const m = mouse.current;
    if (!m) return;
    const dy = e.clientY - m.y;
    if (!m.moved && Math.abs(dy) < 4) return;
    m.moved = true;
    const h = clampWithResistance(m.h - dy, snapH.peek, snapH.full);
    setDragH(h);
    publish(h);
    m.samples.push({ t: e.timeStamp, y: e.clientY });
    if (m.samples.length > 8) m.samples.shift();
  };
  const onHandlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const m = mouse.current;
    mouse.current = null;
    if (!m || !m.moved) return; // a click: onClick handles it
    const v = releaseVelocity(m.samples, e.timeStamp);
    const h = dragH ?? height;
    setDragH(null);
    onSnap(resolveSnap(h, v, snap, snapH));
    snapTick();
    suppressClick.current = true;
  };
  const suppressClick = React.useRef(false);
  // A drag that lands on a snap: the grab bar gives a short stretch (and a
  // light tick on phones that vibrate), so the detent is felt, not guessed.
  const [settleKey, setSettleKey] = React.useState(0);
  function snapTick() {
    setSettleKey((k) => k + 1);
    if (typeof window === 'undefined' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    try {
      navigator.vibrate?.(8);
    } catch {
      /* not allowed: the visual cue is enough */
    }
  }

  // Keyboard: Tab wraps inside the sheet at full; Escape gives the map back.
  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (snap !== 'full' || e.defaultPrevented) return;
    if (e.key === 'Escape') {
      const t = e.target as HTMLElement;
      if (t.closest('input, textarea, [role="menu"], [role="listbox"], [role="dialog"]')) return;
      e.preventDefault();
      onSnap('half');
      return;
    }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (n) => n.offsetParent !== null || n === document.activeElement
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Typing needs room: focusing a field at peek lifts the sheet to half.
  const onFocusCapture = (e: React.FocusEvent<HTMLElement>) => {
    if (snap !== 'peek') return;
    const t = e.target as HTMLElement;
    if (t.matches('input[type="text"], input[type="search"], input:not([type]), textarea')) onSnap('half');
  };

  const handleLabel = snap === 'full' ? 'Show the map' : 'Show more of the planner';
  const hintId = 'planner-sheet-hint';

  return (
    <section
      ref={ref}
      aria-label={label}
      data-testid="planner-sheet"
      data-snap={snap}
      onKeyDown={onKeyDown}
      onFocusCapture={onFocusCapture}
      className={`group/sheet absolute inset-x-0 bottom-0 z-20 flex flex-col overflow-hidden rounded-t-[20px] bg-white shadow-[0_-1px_0_rgba(22,35,58,0.06),0_-12px_32px_-12px_rgba(22,35,58,0.38)] ${
        dragH === null ? 'transition-[height] duration-320 ease-tp-out motion-reduce:transition-none' : ''
      } ${className}`}
      style={{ height: Math.max(0, Math.round(height)) }}
    >
      <button
        type="button"
        aria-label={handleLabel}
        aria-describedby={hintId}
        title="Drag to resize"
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={() => {
          mouse.current = null;
          setDragH(null);
        }}
        onClick={() => {
          if (suppressClick.current) {
            suppressClick.current = false;
            return;
          }
          onSnap(tapSnap(snap));
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            onSnap(stepSnap(snap, 1));
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            onSnap(stepSnap(snap, -1));
          }
        }}
        data-testid="planner-sheet-handle"
        data-sheet-handle=""
        className="relative flex h-6 min-h-0 w-full flex-none cursor-grab touch-none items-center justify-center border-0 bg-transparent active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tp-brand/45"
      >
        {/* A 44px-tall invisible hit area around a 36×4 bar. */}
        <span className="absolute inset-x-0 -bottom-2 top-0" aria-hidden="true" />
        <span
          key={settleKey}
          className={`h-1 w-9 rounded-full bg-[#c9d3e0] transition-colors duration-150 group-data-[dragging=true]/sheet:bg-[#9aa8bb] ${settleKey ? 'tp-handle-settle' : ''}`}
          aria-hidden="true"
        />
      </button>
      <span id={hintId} className="sr-only">
        Drag, tap, or use the up and down arrow keys to resize.
      </span>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </section>
  );
}
